//@ts-nocheck

import React, { useEffect, useMemo } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert, Dialog, DialogTitle, DialogContent, IconButton, Box, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EventIcon from '@mui/icons-material/Event';
import UpdateIcon from '@mui/icons-material/Update';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import { useGetReconciliationFileStatus, ReconciliationFileStatus } from '../../../hooks';
import dayjs from 'dayjs';
import { Loading } from '../../atoms';

type Props = {
  filters: {
    date?: string;
    bankCode?: string;
  } | null;
  shouldFetch: boolean;
  open: boolean;
  onClose: () => void;
};

const StatusChip = ({ status }: { status: string }) => {
  let bgcolor = "#e0e0e0";
  let color = "#757575";
  let label = status;
  let icon = undefined;

  if (status === 'FINISH') {
    bgcolor = "#e8f5e9"; // Light Green
    color = "#2e7d32";   // Dark Green
    label = "BAŞARILI";
    icon = <CheckCircleIcon style={{ color: '#2e7d32' }} />;
  } else if (status === 'FAIL') {
    bgcolor = "#ffebee"; // Light Red
    color = "#c62828";   // Dark Red
    label = "HATALI";
    icon = <ErrorIcon style={{ color: '#c62828' }} />;
  } else if (status === 'START') {
    bgcolor = "#fff3e0"; // Light Orange
    color = "#ef6c00";   // Dark Orange
    label = "BAŞLADI";
    icon = <PlayCircleFilledIcon style={{ color: '#ef6c00' }} />;
  }

  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        fontWeight: 'bold',
        backgroundColor: bgcolor,
        color: color,
        borderRadius: '6px',
        border: '1px solid transparent',
        '& .MuiChip-icon': {
          color: color
        }
      }}
    />
  );
}


const renderTransactionCount = (description: string) => {
  if (!description) return <Typography variant="caption" color="text.disabled">-</Typography>;
  const regex = /Transaction Count\s*:\s*(\d+)/gi;
  const matches = [...description.matchAll(regex)];

  if (matches.length > 0) {
    const counts = matches.map(m => m[1]);
    const uniqueCounts = [...new Set(counts)];
    return (
      <Chip
        label={uniqueCounts.join(' / ')}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', backgroundColor: '#f5f5f5', color: 'primary.main' }}
      />
    );
  }
  return <Typography variant="caption" color="text.disabled">-</Typography>;
}

export const BankFileStatusList: React.FC<Props> = ({ filters, shouldFetch, open, onClose }) => {
  const { mutate: getStatus, data: response, isLoading } = useGetReconciliationFileStatus();

  useEffect(() => {
    if (shouldFetch && filters && open) {
      getStatus({
        date: filters.date,
        bankCode: filters.bankCode
      });
    }
  }, [filters, shouldFetch, open, getStatus]);

  const groupedData = useMemo(() => {
    if (!response?.data) return [];

    const groups = response.data.reduce((acc, item) => {
      const key = item.bankCode;
      if (!acc[key]) {
        acc[key] = {
          bankCode: item.bankCode,
          bankName: item.bankName,
          files: []
        };
      }
      acc[key].files.push(item);
      return acc;
    }, {} as Record<string, { bankCode: string, bankName: string, files: ReconciliationFileStatus[] }>);

    return Object.values(groups);
  }, [response]);

  const hasData = groupedData.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="div" fontWeight="bold" color="text.primary">
            Banka Dosya Durumları
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && <Loading />}

        {!isLoading && shouldFetch && !hasData && response?.isSuccess && (
          <Alert severity="info" sx={{ m: 2 }}>Dosya durumu bulunamadı.</Alert>
        )}

        {hasData && (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Banka</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dosya Adı</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Okunan Veri</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Okuma Tarihi</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', backgroundColor: '#fff', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tamamlanma Tarihi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedData.map((group, groupIndex) => (
                  <React.Fragment key={group.bankCode}>
                    {group.files.map((file, fileIndex) => (
                      <TableRow
                        key={`${group.bankCode}-${fileIndex}`}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: '2px solid #eee' }
                        }}
                      >
                        {/* Render Bank Cell only for the first row of the group */}
                        {fileIndex === 0 && (
                          <TableCell
                            rowSpan={group.files.length}
                            sx={{
                              verticalAlign: 'top',
                              backgroundColor: '#fafafa', // Subtle background for grouping column
                              borderRight: '1px solid #f0f0f0',
                              borderBottom: '2px solid #eee',
                              width: '20%'
                            }}
                          >
                            <Box display="flex" flexDirection="column" gap={0.5} mt={1.5} pl={1}>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography variant="subtitle1" fontWeight="500" color="text.primary">
                                  {group.bankName}
                                </Typography>
                              </Box>
                              <Box>
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: '#eee',
                                    color: 'text.secondary',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {group.bankCode}
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.primary">
                              {file.fileName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{renderTransactionCount(file.description)}</TableCell>
                        <TableCell>
                          <StatusChip status={file.status} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <EventIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary">
                              {dayjs(file.createDate).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <UpdateIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary">
                              {file.updateDate ? dayjs(file.updateDate).format('DD.MM.YYYY HH:mm') : '-'}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
